import { useDropzone } from 'react-dropzone';
import { RefreshIconOutline, TrashIcon } from '@/lib';
import { IMAGE_UPLOAD_ACCEPT, useImageUpload } from '../../hooks/useImageUpload';
import type { ReactNode } from 'react';
import { useState } from 'react';

type FieldProps = {
  label: string;
  children: ReactNode;
};

export function Field({ label, children }: FieldProps) {
  return (
    <div className='ee-field'>
      <label>{label}</label>
      {children}
    </div>
  );
}

type ColorRowProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

export function ColorRow({ label, value, onChange, disabled }: ColorRowProps) {
  return (
    <div className='ee-colorrow'>
      <span>{label}</span>
      <label className='ee-swatch' style={{ background: value }}>
        <input type='color' value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
      </label>
      <input
        className='input ee-colorhex'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  );
}

type SegOption = { v: string; label: string };

type SegGroupProps = {
  value: string;
  onChange: (v: string) => void;
  options: SegOption[];
  disabled?: boolean;
};

export function SegGroup({ value, onChange, options, disabled }: SegGroupProps) {
  return (
    <div className='ee-seggroup'>
      {options.map((o) => (
        <button key={o.v} type='button' className={value === o.v ? 'is-on' : ''} onClick={() => onChange(o.v)} disabled={disabled}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

type QuadProps = {
  values: [number, number, number, number];
  grouped: boolean;
  onChange: (top: number, right: number, bottom: number, left: number) => void;
  disabled?: boolean;
};

export function Quad({ values, grouped, onChange, disabled }: QuadProps) {
  const [t, r, b, l] = values;

  if (grouped) {
    return (
      <div className='ee-quad-grouped'>
        <span>↕↔</span>
        <input
          className='input'
          type='number'
          value={t}
          disabled={disabled}
          onChange={(e) => {
            const v = +e.target.value;
            onChange(v, v, v, v);
          }}
        />
        <div className='ee-unit'>px</div>
      </div>
    );
  }

  return (
    <div className='ee-quad'>
      <div className='ee-quad__cell'>
        <span>↑</span>
        <input type='number' value={t} disabled={disabled} onChange={(e) => onChange(+e.target.value, r, b, l)} />
      </div>
      <div className='ee-quad__cell'>
        <span>→</span>
        <input type='number' value={r} disabled={disabled} onChange={(e) => onChange(t, +e.target.value, b, l)} />
      </div>
      <div className='ee-quad__cell'>
        <span>↓</span>
        <input type='number' value={b} disabled={disabled} onChange={(e) => onChange(t, r, +e.target.value, l)} />
      </div>
      <div className='ee-quad__cell'>
        <span>←</span>
        <input type='number' value={l} disabled={disabled} onChange={(e) => onChange(t, r, b, +e.target.value)} />
      </div>
    </div>
  );
}

type ImageUploaderProps = {
  src?: string;
  onUploadImage: (file: File) => Promise<string>;
  onUploaded: (src: string) => void;
  onClear?: () => void;
  disabled?: boolean;
};

const formatUploadSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes % (1024 * 1024) === 0 ? 0 : 1)} MB`;
};

const fileNameFromUrl = (url: string): string => {
  try {
    const name = decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
    return name || 'image';
  } catch {
    return 'image';
  }
};

export function ImageUploader({ src, onUploadImage, onUploaded, onClear, disabled }: ImageUploaderProps) {
  const { upload, uploading, error } = useImageUpload(onUploadImage, onUploaded);
  const [fileMeta, setFileMeta] = useState<{ name: string; size: number }>();
  const { getRootProps, getInputProps, open } = useDropzone({
    accept: IMAGE_UPLOAD_ACCEPT,
    multiple: false,
    disabled: disabled || uploading,
    // Rejected files go through too, so the host's validation reports why.
    onDrop: (accepted, rejected) => {
      const file = accepted[0] ?? rejected[0]?.file;
      if (!file) return;
      setFileMeta({ name: file.name, size: file.size });
      void upload(file);
    },
  });

  const displayName = fileMeta?.name ?? (src ? fileNameFromUrl(src) : '');
  const displaySize = fileMeta ? formatUploadSize(fileMeta.size) : undefined;

  if (src) {
    return (
      <div className='ee-uploader-wrap'>
        <p className='ee-uploader__hint-top'>JPG or PNG, max 5 MB. Upload from your computer or drag & drop.</p>
        <div className='ee-uploader-card'>
          <img src={src} alt='' className='ee-uploader-card__thumb' />
          <div className='ee-uploader-card__meta'>
            <div className='ee-uploader-card__name' title={displayName}>
              {displayName}
            </div>
            {displaySize ? <div className='ee-uploader-card__size'>{displaySize}</div> : null}
          </div>
          {!disabled && (
            <div className='ee-uploader-card__actions'>
              <button type='button' className='ee-uploader-card__replace' disabled={uploading} onClick={() => open()}>
                <RefreshIconOutline className='ee-uploader-card__replace-icon' />
                Replace
              </button>
              <button
                type='button'
                className='ee-uploader-card__delete'
                aria-label='Remove'
                disabled={uploading}
                onClick={() => {
                  setFileMeta(undefined);
                  onClear?.();
                }}
              >
                <TrashIcon className='ee-uploader-card__delete-icon' />
              </button>
              <input {...getInputProps()} />
            </div>
          )}
        </div>
        {error ? <div className='ee-uploader__hint' style={{ color: '#DC2626' }}>{error}</div> : null}
      </div>
    );
  }

  return (
    <div className='ee-uploader-wrap'>
      <p className='ee-uploader__hint-top'>JPG or PNG, max 5 MB. Upload from your computer or drag & drop.</p>
      <div {...getRootProps({ className: 'ee-uploader' })}>
        <input {...getInputProps()} />
        <div className='ee-uploader__icon'>⬆</div>
        <div className='ee-uploader__main'>
          {uploading ? 'Uploading…' : (
            <>
              Drop image here or <span className='ee-uploader__browse'>browse</span>
            </>
          )}
        </div>
        <div className='ee-uploader__hint' style={error ? { color: '#DC2626' } : undefined}>
          {error ?? null}
        </div>
      </div>
    </div>
  );
}
